import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, MapPin, Plus, CheckCircle2, AlertCircle } from 'lucide-react';
import { useAppointmentTypes } from '@/hooks/useAppointmentTypes';

export default function DayDetailDialog({ open, onClose, date, appointments, teams, onSelectAppointment, onNewAppointment, isManagerOrAdmin }) {
  const { getTypeInfo } = useAppointmentTypes();
  
  if (!date) return null;

  const formattedDate = date.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  const getTeamInfo = (teamId) => {
    return teams.find(t => t.id === teamId) || { name: 'Unknown', color: '#64748b' };
  };

  // Sort appointments by start time
  const sortedAppointments = [...appointments].sort((a, b) => 
    a.start_time.localeCompare(b.start_time)
  );

  const scheduledCount = appointments.filter(a => a.status !== 'completed').length;
  const completedCount = appointments.filter(a => a.status === 'completed').length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh]" data-testid="day-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <Calendar className="w-5 h-5 text-[#2563eb]" />
            {formattedDate}
          </DialogTitle>
        </DialogHeader>

        <div className="py-2">
          {/* Summary */}
          <div className="flex items-center gap-3 mb-4 text-sm">
            <Badge variant="outline" className="gap-1">
              <span className="font-bold">{appointments.length}</span> Total
            </Badge>
            {scheduledCount > 0 && (
              <Badge variant="outline" className="gap-1 text-amber-600 border-amber-300">
                <AlertCircle size={12} />
                {scheduledCount} Scheduled
              </Badge>
            )}
            {completedCount > 0 && (
              <Badge variant="outline" className="gap-1 text-green-600 border-green-300">
                <CheckCircle2 size={12} />
                {completedCount} Completed
              </Badge>
            )}
          </div>

          {/* Appointments List */}
          {sortedAppointments.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No appointments scheduled</p>
              {isManagerOrAdmin && (
                <Button 
                  className="mt-4 bg-[#2563eb] hover:bg-[#1d4ed8]"
                  onClick={() => {
                    onClose();
                    onNewAppointment(date);
                  }}
                >
                  <Plus size={16} className="mr-2" />
                  Add Appointment
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-2">
              {sortedAppointments.map((apt) => {
                const team = getTeamInfo(apt.team_id);
                const isCompleted = apt.status === 'completed';
                const typeInfo = getTypeInfo(apt.appointment_type);
                const TypeIcon = typeInfo.icon;
                
                return (
                  <div
                    key={apt.id}
                    onClick={() => {
                      onClose();
                      onSelectAppointment(apt);
                    }}
                    className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                      isCompleted 
                        ? 'bg-green-50/50 border-green-200 hover:border-green-300' 
                        : 'border-slate-200 hover:border-[#2563eb]'
                    }`}
                    style={{ backgroundColor: isCompleted ? undefined : typeInfo.bgColor }}
                    data-testid={`day-appointment-${apt.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Time */}
                      <div className="text-center min-w-[50px]">
                        <p className="text-sm font-bold text-slate-800">{apt.start_time}</p>
                        <p className="text-xs text-slate-400">{apt.end_time}</p>
                      </div>

                      {/* Type Icon Bar */}
                      <div 
                        className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: typeInfo.color }}
                      >
                        <TypeIcon size={16} className="text-white" />
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-slate-800 truncate">{apt.title}</h4>
                          {isCompleted && (
                            <CheckCircle2 size={14} className="text-green-600 flex-shrink-0" />
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-slate-500">{team.name}</span>
                          <span style={{ color: typeInfo.color }}>• {typeInfo.label}</span>
                        </div>
                        {apt.location && (
                          <div className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                            <MapPin size={10} />
                            <span className="truncate">{apt.location}</span>
                          </div>
                        )}
                        {apt.assigned_to_user_name && (
                          <p className="text-xs text-blue-600 mt-1">
                            Assigned: {apt.assigned_to_user_name}
                          </p>
                        )}
                      </div>

                      {/* Duration */}
                      <div className="text-right">
                        <Badge variant="secondary" className="text-xs">
                          {apt.duration}m
                        </Badge>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Button for days with appointments */}
          {sortedAppointments.length > 0 && isManagerOrAdmin && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => {
                  onClose();
                  onNewAppointment(date);
                }}
              >
                <Plus size={16} className="mr-2" />
                Add Appointment to This Day
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
